import { useCallback, useMemo } from "react";
import { API_BASE, DEFAULT_HEADERS } from "../../services/apiClient";
import {
  getCachedUserBundle,
  getCurrentUserId,
} from "../../services/authService";
import { ANALYTICS_LIKES_ENABLED } from "../utils/analytics";
import { trackEvent } from "../../services/trackingService";

const isProd = process.env.NODE_ENV === "production";
const debugLog = (...args) => {
  if (!isProd) {
    // eslint-disable-next-line no-console
    console.log("[Likes]", ...args);
  }
};

export const toIntId = (val) => {
  if (val == null) return NaN;
  const str = String(val);
  if (/^\d+$/.test(str)) return parseInt(str, 10);
  const m = str.match(/\d+/);
  return m ? parseInt(m[0], 10) : NaN;
};

export const useLikes = (toast) => {
  const resolvedApiKey = useMemo(() => {
    if (typeof process !== "undefined" && process?.env) {
      const envKeys = [
        "REACT_APP_API_KEY",
        "NEXT_PUBLIC_API_KEY",
        "VITE_API_KEY",
        "API_KEY",
      ];
      for (const key of envKeys) {
        const value = process.env[key];
        if (value) return value;
      }
    }
    return DEFAULT_HEADERS?.["X-API-KEY"] || "";
  }, []);

  const apiHeaders = useCallback(
    (extra = {}) => {
      const token =
        localStorage.getItem("authToken") ||
        sessionStorage.getItem("authToken");
      const uid = getCurrentUserId();
      return {
        "Content-Type": "application/json",
        ...DEFAULT_HEADERS,
        ...(resolvedApiKey ? { "X-API-KEY": resolvedApiKey } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(uid ? { "X-User-Id": String(uid) } : {}),
        ...extra,
      };
    },
    [resolvedApiKey]
  );

  const getLocalUserInfo = useCallback(() => {
    const uid = getCurrentUserId();
    if (!uid) return null;
    const bundle = getCachedUserBundle();
    const profile = bundle?.profile;
    const user = bundle?.user;
    const first = profile?.first_name || user?.first_name || "";
    const last = profile?.last_name || user?.last_name || "";
    const name = `${first} ${last}`.trim() || user?.email || profile?.email || "Me";
    const avatar_url = profile?.avatar_url || user?.avatar_url || "";
    return {
      id: uid,
      name,
      avatar_url,
    };
  }, []);

  const sendLike = useCallback(
    async ({ type, id, like, user_id }) => {
      if (!ANALYTICS_LIKES_ENABLED) {
        debugLog("sendLike skipped: ANALYTICS_LIKES_ENABLED is false", {
          type,
          id,
          like,
          user_id,
        });
        return { skipped: true };
      }
      const body = { type, id, like, user_id };
      const res = await fetch(`${API_BASE}/analytics/like`, {
        method: "POST",
        headers: apiHeaders(),
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`Like endpoint failed (${res.status}) ${text}`);
      }
      return res.json().catch(() => ({}));
    },
    [apiHeaders]
  );

  const fetchLikes = useCallback(
    async ({ type, id, user_id }) => {
      if (!ANALYTICS_LIKES_ENABLED) {
        return { like_count: 0, liked_by_me: false };
      }
      const params = new URLSearchParams({ type: String(type), id: String(id) });
      if (user_id != null) params.set("user_id", String(user_id));
      const res = await fetch(
        `${API_BASE}/analytics/likes?${params.toString()}`,
        {
          method: "GET",
          headers: apiHeaders(),
        }
      );
      if (!res.ok) throw new Error(`likes GET failed ${res.status}`);
      return res.json();
    },
    [apiHeaders]
  );

  const hydrateLikes = useCallback(
    async (items, setCombinedPosts) => {
      // Temporarily disabled: skip fetching likes from analytics endpoint.
      return;
    },
    [fetchLikes]
  );

  const handleToggleLike = useCallback(
    async (item, setCombinedPosts) => {
      try {
        if (!item || typeof setCombinedPosts !== "function") return;
        const uid = getCurrentUserId();
        if (!uid) {
          toast?.({ title: "Please sign in", status: "warning" });
          return;
        }
        const currentUserInfo = getLocalUserInfo();
        const itemType = (item.type || "post").toString().trim().toLowerCase();
        const rawId = item.id ?? item.item_id ?? item.post_id;
        const intId = toIntId(rawId);
        if (!Number.isFinite(intId)) {
          toast?.({ title: "Invalid content id for like", status: "error" });
          return;
        }

        const nextLike = item.liked_by_me ? 0 : 1;
        const updateLikers = (likersList, add) => {
          const safeList = Array.isArray(likersList) ? likersList : [];
          if (!currentUserInfo) return safeList;
          const filtered = safeList.filter(
            (l) => String(l?.id ?? l?.user_id ?? "") !== String(currentUserInfo.id)
          );
          if (add) {
            return [
              {
                id: currentUserInfo.id,
                name: currentUserInfo.name,
                avatar_url: currentUserInfo.avatar_url,
              },
              ...filtered,
            ].slice(0, 10);
          }
          return filtered;
        };

        setCombinedPosts((prev) =>
          prev.map((it) => {
            const itId = toIntId(it.id ?? it.item_id ?? it.post_id);
            if (it.type === itemType && Number.isFinite(itId) && itId === intId) {
              const delta = nextLike === 1 ? 1 : -1;
              const nextCount = Math.max(0, (it.like_count || 0) + delta);
              return {
                ...it,
                liked_by_me: nextLike === 1,
                like_count: nextCount,
                likers: updateLikers(it.likers, nextLike === 1),
              };
            }
            return it;
          })
        );

        try {
          debugLog("Sending like to backend", {
            type: itemType,
            id: intId,
            like: nextLike,
            user_id: uid,
            featureEnabled: ANALYTICS_LIKES_ENABLED,
          });
          await sendLike({
            type: itemType,
            id: intId,
            like: nextLike,
            user_id: uid,
          });
          trackEvent(nextLike === 1 ? "like" : "unlike", {
            targetType: itemType,
            targetId: String(intId),
          });
        } catch (err) {
          setCombinedPosts((prev) =>
            prev.map((it) => {
              const itId = toIntId(it.id ?? it.item_id ?? it.post_id);
              if (it.type === itemType && Number.isFinite(itId) && itId === intId) {
                const delta = nextLike === 1 ? -1 : 1;
                const nextCount = Math.max(0, (it.like_count || 0) + delta);
                return {
                  ...it,
                  liked_by_me: !(nextLike === 1),
                  like_count: nextCount,
                  likers: updateLikers(it.likers, nextLike !== 1),
                };
              }
              return it;
            })
          );
          console.error("sendLike failed", err);
          toast?.({ title: "Could not record like", status: "error" });
        }
      } catch (e) {
        console.error("handleToggleLike error", e);
        toast?.({ title: "Unexpected error", status: "error" });
      }
    },
    [getLocalUserInfo, sendLike, toast]
  );

  return { handleToggleLike, hydrateLikes };
};
